import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Calendar, User } from 'lucide-react';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';
import { blog } from '../dashboard/api';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const BlogPage = ({
  onBack,
  onOpenPost,
  onNavigateToDemo,
  onNavigateToOutfit,
  onNavigateToBlog,
  onNavigateToDashboard,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookiePolicy,
  onNavigateToApiDocs,
}) => {
  const [data, setData] = useState({ posts: [], pagination: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    blog
      .list(page, 10)
      .then((res) => {
        if (!cancelled) {
          setData({ posts: res.posts, pagination: res.pagination });
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load blog');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [page]);

  const handlePostClick = (slug) => {
    if (onOpenPost) onOpenPost(slug);
    else window.history.pushState({}, '', `/blog/${slug}`);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white font-sans antialiased flex flex-col">
      <SiteHeader
        onLogoClick={onBack}
        onOpenDemo={onNavigateToDemo}
        onOutfitClick={onNavigateToOutfit}
        onBlogClick={onNavigateToBlog}
        onLoginClick={onNavigateToDashboard}
      />
      <motion.main
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="flex-1 max-w-3xl mx-auto px-6 pt-20 md:pt-28 pb-12 w-full"
      >
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Blog</h1>
        <p className="text-white/50 text-sm mb-10">
          Updates, guides, and news from VisionLive.
        </p>

        {error && (
          <div className="rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 px-4 py-3 text-sm mb-8">
            {error}
          </div>
        )}

        {loading && (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-6 animate-pulse">
                <div className="h-6 bg-white/10 rounded w-3/4 mb-3" />
                <div className="h-4 bg-white/10 rounded w-full mb-2" />
                <div className="h-4 bg-white/10 rounded w-2/3" />
              </div>
            ))}
          </div>
        )}

        {!loading && !error && data.posts.length === 0 && (
          <p className="text-white/50">No posts yet. Check back soon.</p>
        )}

        {!loading && !error && data.posts.length > 0 && (
          <div className="space-y-6">
            {data.posts.map((post) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/15 transition-all overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => handlePostClick(post.slug)}
                  className="w-full text-left block"
                >
                  {post.mainImageUrl && (
                    <div className="w-full aspect-video overflow-hidden bg-white/5">
                      <img
                        src={post.mainImageUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h2 className="text-lg font-semibold text-white mb-2 group-hover:text-white">
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className="text-white/60 text-sm line-clamp-2 mb-3">
                        {post.excerpt}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/40">
                      {post.authorName && (
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {post.authorName}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(post.publishedAt || post.createdAt)}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 mt-3 text-sm text-[#3B82F6] font-medium">
                      Read more
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </button>
              </motion.article>
            ))}

            {data.pagination && data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-8">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-4 py-2 rounded-lg bg-white/10 text-white/80 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/15"
                >
                  Previous
                </button>
                <span className="text-white/50 text-sm px-2">
                  {page} of {data.pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= data.pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-4 py-2 rounded-lg bg-white/10 text-white/80 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/15"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </motion.main>
      <SiteFooter
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookiePolicy={onNavigateToCookiePolicy}
        onNavigateToApiDocs={onNavigateToApiDocs}
        onNavigateToBlog={onNavigateToBlog}
      />
    </div>
  );
};

export default BlogPage;
